import { produce } from 'immer';
import { EndpointOptions } from 'lib-shared-auth';
import { config } from 'lib-shared-backend-config';

import { api } from 'app/app.api';

import {
  CreateAttachmentApiArg,
  CreateAttachmentApiResponse,
  CreateScheduledConversationsCsvApiArg,
  CreateScheduledConversationsCsvApiResponse,
  ReadFileApiArg,
  ReadFileApiResponse,
} from './types/filesApi.types';

export function transformFilesApiResponse(response: any): ReadFileApiResponse {
  return {
    data: {
      content: response,
    },
    meta: {},
  };
}

export const filesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createAttachment: builder.mutation<
      CreateAttachmentApiResponse,
      CreateAttachmentApiArg
    >({
      query: ({ access, file }) =>
        getFileFormDataEndpointOptions(`${access}/attachment`, file),
      // Technically we are cheating here because the raw response doesn't have
      // the `absolute_uri` field.
      transformResponse: (response: CreateAttachmentApiResponse) =>
        produce(response, (draft) => {
          draft.data.absolute_uri = `${config.url.files}${response.data.relative_uri}`;
        }),
    }),
    createScheduledConversationsCsv: builder.mutation<
      CreateScheduledConversationsCsvApiResponse,
      CreateScheduledConversationsCsvApiArg
    >({
      query: ({ file }) =>
        getFileFormDataEndpointOptions(
          'private/scheduled_conversation_input/callback',
          file,
        ),
    }),
    readFile: builder.query<ReadFileApiResponse, ReadFileApiArg>({
      query: ({ path }) => ({
        type: 'files',
        path: `${path}`,
      }),
      transformResponse: transformFilesApiResponse,
    }),
  }),
});

/**
 * Returns the endpoint options for file form data mutation.
 *
 * @param path The endpoint path.
 * @param file The form data file.
 */
function getFileFormDataEndpointOptions(
  path: string,
  file: File,
): EndpointOptions {
  const body = new FormData();
  body.append('file', file);

  return {
    path,
    method: 'POST',
    type: 'files',
    contentType: 'multipart/form-data',
    body,
  };
}
